import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Box, CardContent, CardHeader, cardHeaderClasses } from '@mui/material';

import {
	usePaymentMethod,
	useShallowEqualSelector,
	useSubscribed,
	useSubscriptionInfo
} from '@/hooks';
import { PAYMENT_STATUS } from '@/models/payment';
import { StatusChip } from './StatusChip';
import { CancelSubscriptionModal } from './CancelSubscriptionModal';
import { ReactivateSubscriptionModal } from './ReactivateSubscriptionModal';
import Description from './Description';
import { ManageSubscriptionModal } from './ManageSubscriptionModal';

import { useUserPermissions } from '../../../hooks/hooks';

export function ActivePlan({ onClickUpdatePayment = () => {} }) {
	const location = useLocation();
	const { t } = useTranslation();
	const { getIsAdminOrOwner } = useUserPermissions();

	const subscribed = useSubscribed();
	const subscriptionInfo = useSubscriptionInfo();
	const { hasPaymentMethod } = usePaymentMethod();

	const { cancellationRequested, isCanceled, isTrial, isExpired, isPending } =
		subscriptionInfo || {};

	const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
		useState(false);
	const [reactivateSubscriptionModalOpen, setReactivateSubscriptionModalOpen] =
		useState(false);
	const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(
		() => location.state?.subscriptionModal || false
	);

	const { lastPayment } = useShallowEqualSelector(state => ({
		lastPayment: state.billing?.rows?.[0]
	}));

	const isAdminOrOwner = getIsAdminOrOwner();

	const willCancel = cancellationRequested && !isCanceled;

	const showChangePlan =
		isTrial ||
		cancellationRequested ||
		isExpired ||
		isCanceled ||
		(isPending && lastPayment?.status === PAYMENT_STATUS.ERROR);

	const buttonsSx = { width: ['fit-content'], px: 1 };

	return (
		<>
			<ManageSubscriptionModal
				subscriptionInfo={subscriptionInfo}
				open={manageSubscriptionOpen}
				onClose={() => setManageSubscriptionOpen(false)}
			/>
			<CancelSubscriptionModal
				open={cancelSubscriptionModalOpen}
				onClose={() => setCancelSubscriptionModalOpen(false)}
			/>
			<ReactivateSubscriptionModal
				open={reactivateSubscriptionModalOpen}
				onClose={() => setReactivateSubscriptionModalOpen(false)}
			/>
			<Card
				sx={{
					flex: 1,
					display: 'flex',
					flexDirection: 'column',

					'& .MuiCardHeader-root': {
						alignItems: 'flex-start'
					}
				}}
			>
				<CardHeader
					title={t('common.subscription')}
					action={<StatusChip {...subscriptionInfo} />}
					subheader={<Description subscriptionInfo={subscriptionInfo} />}
					sx={{
						flex: 1,
						[`.${cardHeaderClasses.subheader}`]: {
							mt: '12px'
						},
						[`.${cardHeaderClasses.action}`]: {
							m: 0,
							mr: { sm: 0.5 }
						}
					}}
				/>
				<CardContent sx={{ pt: 1 }}>
					<Box
						sx={{
							display: 'flex',
							flexDirection: { xs: 'column', sm: 'row' },
							justifyContent: 'space-between',
							gap: 1.5
						}}
					>
						{isAdminOrOwner &&
							subscribed &&
							(willCancel ? (
								<Button
									variant="outlined"
									onClick={() => {
										if (!hasPaymentMethod && !isCanceled) {
											return onClickUpdatePayment();
										}

										setReactivateSubscriptionModalOpen(true);
									}}
									sx={buttonsSx}
								>
									{t('billing.subscription.reactivate')}
								</Button>
							) : (
								<>
									<Button
										variant="outlined"
										onClick={() =>
											setCancelSubscriptionModalOpen(true)
										}
										sx={buttonsSx}
									>
										{t('billing.subscription.cancel')}
									</Button>

									{showChangePlan && (
										<Button
											variant="outlined"
											onClick={() => setManageSubscriptionOpen(true)}
											sx={buttonsSx}
										>
											{t('billing.changePlan')}
										</Button>
									)}
								</>
							))}

						{isAdminOrOwner && !subscribed && (
							<Button
								variant="outlined"
								onClick={() => setManageSubscriptionOpen(true)}
								sx={buttonsSx}
							>
								{t('billing.manageSubscription')}
							</Button>
						)}
					</Box>
				</CardContent>
			</Card>
		</>
	);
}
